import Loading, { LoadingBall } from '@ingka/loading';

import InlineMessage from '@ingka/inline-message';
import React from 'react';
import Status from '@ingka/status';
import infoIcon from '@ingka/ssr-icon/paths/information';
import { tokens } from '../../../shared/styles/core/tokens';
import { useConnectedItemsState } from '../../items/state/connected-items.state';

export const ConnectedItemsStatus = () => {
  const {
    refreshRequests,
    showStatusDialogue,
    getItemsCompleted,
    getParentItems,
    getChildrenForParent,
    getItemResponseStatus,
    clearResult,
    closeDialogue,
    operationType
  } = useConnectedItemsState();

  if (!showStatusDialogue) {
    return null;
  }

  function shouldShowLoading(itemNo: string): boolean {
    const response = getItemResponseStatus(itemNo);
    const allCompletedItems = getItemsCompleted();

    if (response === undefined) {
      return allCompletedItems.length === 0;
    }

    return !response;
  }

  function getStatusColour(itemNo: string): string {
    const response = getItemResponseStatus(itemNo);
    const children = getChildrenForParent(itemNo);

    if (response === undefined) {
      if (children && children.length > 0) {
        const allSuccessful = children.every(child => child.status);
        return allSuccessful ? "green" : "red";
      }
      return "red";
    }

    return response.status ? "green" : "red";
  }

  function getStatusText(itemNo: string): string {
    const response = getItemResponseStatus(itemNo);
    const children = getChildrenForParent(itemNo);

    if (response === undefined) {
      if (children && children.length > 0) {
        const successful = children.filter(child => child.status).length;
        const failed = children.length - successful;

        if (successful === children.length) {
          return operationType === 'reset'
            ? "Reset complete"
            : "Refresh complete";
        } else if (failed > 0) {
          return `${successful} ok, ${failed} failed`;
        }
      }
      return "Pending...";
    }

    if (!response) return "Pending...";
    if (!response.status) return response.message || "Failed";
    return response.message || "OK";
  }

  const parentItems = operationType === 'refresh'
    ? getParentItems()
    : refreshRequests;

  const completedItems = getItemsCompleted();
  const successCount = completedItems.filter(item => item.status).length;
  const failedCount = completedItems.length - successCount;

  const title = operationType === 'reset'
    ? "Resetting connected items..."
    : "Refreshing items from PIA...";

  const getStatusSummary = () => {
    if (completedItems.length === 0) return "";

    const messages = [];
    if (operationType === 'reset') {
      if (successCount > 0) {
        messages.push(`${successCount} ${successCount === 1 ? 'item' : 'items'} reset successfully`);
      }
      if (failedCount > 0) {
        messages.push(`${failedCount} ${failedCount === 1 ? 'item' : 'items'} failed to reset`);
      }
    } else {
      if (successCount > 0) {
        messages.push(`${successCount} ${successCount === 1 ? 'item' : 'items'} refreshed`);
      }
      if (failedCount > 0) {
        messages.push(`${failedCount} failed`);
      }
    }
    return messages.join(', ');
  };

  return (
    <InlineMessage
      style={{ position: "fixed", zIndex: 1000, left: "40%" }}
      body={
        <>
          {completedItems.length} of {refreshRequests.length} completed. {getStatusSummary()}
          <br />
          <div style={{ maxHeight: "20rem", overflow: "auto" }}>
            <table style={{ borderSpacing: "0.5em" }}>
              <tbody>
                {parentItems.map((parentItemNo) => (
                  <React.Fragment key={parentItemNo}>
                    <tr>
                      <td style={{ verticalAlign: "middle" }}>
                        {shouldShowLoading(parentItemNo) ? (
                          <Loading>
                            <LoadingBall color="emphasised" size="small" />
                          </Loading>
                        ) : (
                          <Status statusColor={getStatusColour(parentItemNo)} />
                        )}
                      </td>
                      <td style={{ fontWeight: tokens.font.weight.bold }}>{parentItemNo}</td>
                      <td>{getStatusText(parentItemNo)}</td>
                    </tr>

                    {getChildrenForParent(parentItemNo).map((childItem) => (
                      <tr key={`${parentItemNo}-${childItem.itemNo}`}>
                        <td style={{ verticalAlign: "middle", paddingLeft: "1em" }}>
                          <Status statusColor={childItem.status ? "green" : "red"} />
                        </td>
                        <td style={{ paddingLeft: "1em" }}>{childItem.itemNo}</td>
                        <td style={{ color: childItem.status ? 'inherit' : 'red' }}>
                          {childItem.message}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            <button
              onClick={() => clearResult()}
              style={{ cursor: "pointer", background: "none", border: "none", color: "inherit", textDecoration: "underline" }}
            >
              Clear
            </button>
            <button
              onClick={() => closeDialogue()}
              style={{ cursor: "pointer", background: "none", border: "none", color: "inherit", textDecoration: "underline", float: "right" }}
            >
              Close
            </button>
          </p>
        </>
      }
      ssrIcon={infoIcon}
      title={title}
      variant="informative"
    />
  );
};
